import * as React from 'react';
import {useContext, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import * as Panel2 from './panel';
import * as PanelLib from './panellib/libpanel';
import makeComp from '../../util/profiler';
import {Button, Popup, Modal} from 'semantic-ui-react';
import * as Types from './types';
import {PanelExportContext} from './PanelExportContext';
import {ComputeGraphViz} from './ComputeGraphViz';
import * as CG from './cg/graph';
import * as HL from './cg/hl';
import * as S from './PanelComp.styles';

interface PanelCompProps {
  panelSpec: Panel2.PanelSpecNode;
  input: Panel2.PanelInput;
  inputType: Types.Type;
  context: Panel2.PanelContext;
  config: any;
  loading: boolean;
  configMode: boolean;
  updateConfig(partialConfig: Partial<any>): void;
  updateContext(partialConfig: Partial<Panel2.PanelContext>): void;
}

function ErrorFallback(props: {error?: Error}) {
  const {error} = props;
  return (
    <div role="alert">
      <p>Something went wrong</p>
      <pre>{error?.message}</pre>
    </div>
  );
}

export const PanelComp2 = makeComp(
  (props: PanelCompProps) => {
    // This is useful so I'm leaving it:
    // useTraceUpdate(props.panelSpec.id, props);
    return (
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <PanelComp2Inner {...props} />
      </ErrorBoundary>
    );
  },
  {id: 'PanelComp', memo: true}
);

export const PanelComp2Inner = makeComp(
  (props: PanelCompProps) => {
    const {panelSpec} = props;
    if (PanelLib.isWithChild(panelSpec)) {
      if (props.configMode) {
        const ConfigComp = panelSpec.ConfigComponent;
        if (ConfigComp == null) {
          return <PanelComp2 {...props} panelSpec={panelSpec.child} />;
        }
        return (
          <ControlWrapper panelProps={props}>
            <ConfigComp
              input={props.input}
              context={props.context}
              loading={props.loading}
              inputType={props.inputType}
              child={panelSpec.child}
              configMode={props.configMode}
              config={props.config}
              updateConfig={props.updateConfig}
              updateContext={props.updateContext}
            />
          </ControlWrapper>
        );
      }
      const Comp = panelSpec.Component;
      return (
        <ControlWrapper panelProps={props}>
          <Comp
            input={props.input}
            context={props.context}
            loading={props.loading}
            inputType={props.inputType}
            child={panelSpec.child}
            configMode={props.configMode}
            config={props.config}
            updateConfig={props.updateConfig}
            updateContext={props.updateContext}
          />
        </ControlWrapper>
      );
    } else {
      if (props.configMode) {
        const ConfigComp = panelSpec.ConfigComponent;
        if (ConfigComp == null) {
          return null;
        }
        return (
          <ControlWrapper panelProps={props}>
            <ConfigComp
              input={props.input}
              context={props.context}
              loading={props.loading}
              configMode={props.configMode}
              config={props.config}
              updateConfig={props.updateConfig}
              updateContext={props.updateContext}
            />
          </ControlWrapper>
        );
      }
      const Comp = panelSpec.Component;
      return (
        <ControlWrapper panelProps={props}>
          <Comp
            configMode={props.configMode}
            context={props.context}
            loading={props.loading}
            input={props.input}
            config={props.config}
            updateConfig={props.updateConfig}
            updateContext={props.updateContext}
          />
        </ControlWrapper>
      );
    }
  },
  {id: 'PanelComp'}
);

interface ControlWrapperProps {
  panelProps: PanelCompProps;
}

const ControlWrapper: React.FC<ControlWrapperProps> = ({
  panelProps,
  children,
}) => {
  const {devMode} = useContext(PanelExportContext);
  const [fullscreen, setFullscreen] = useState(false);
  const [hovering, setHovering] = useState(false);
  const canFullscreen =
    !panelProps.configMode &&
    'canFullscreen' in panelProps.panelSpec &&
    panelProps.panelSpec.canFullscreen;
  const canShowDevQueryPopup = devMode && !panelProps.configMode;
  const showControls = canFullscreen || canShowDevQueryPopup;

  return showControls ? (
    <S.ControlWrapper
      hovering={hovering}
      onMouseEnter={() => {
        if (!hovering) {
          setHovering(true);
        }
      }}
      onMouseLeave={() => {
        if (hovering) {
          setHovering(false);
        }
      }}
      canFullscreen={canFullscreen}>
      <S.ControlWrapperBar hovering={hovering}>
        {canShowDevQueryPopup && <DevQueryPopup panelProps={panelProps} />}
        {canFullscreen && (
          <S.IconButton
            data-test="panel-fullscreen-button"
            onClick={() => setFullscreen(true)}
            style={{cursor: 'pointer'}}>
            <S.FullscreenButton />
          </S.IconButton>
        )}
      </S.ControlWrapperBar>
      <S.ControlWrapperContent canFullscreen={canFullscreen}>
        <Modal
          open={fullscreen}
          onClose={() => setFullscreen(false)}
          onOpen={() => setFullscreen(true)}>
          <Modal.Content
            style={{
              height: 'calc(90vh - 73px)',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'stretch',
              alignItems: 'stretch',
            }}>
            {children}
          </Modal.Content>
          <Modal.Actions>
            <Button
              data-test="panel-fullscreen-modal-close-button"
              onClick={() => setFullscreen(false)}>
              Close
            </Button>
          </Modal.Actions>
        </Modal>
        {children}
      </S.ControlWrapperContent>
    </S.ControlWrapper>
  ) : (
    <S.ControlWrapperContent>{children}</S.ControlWrapperContent>
  );
};

interface DevQueryPopupProps {
  panelProps: PanelCompProps;
}
const DevQueryPopup: React.FC<DevQueryPopupProps> = makeComp(
  props => {
    const [queryVisType, setQueryVisType] = useState<'string' | 'dag'>(
      'string'
    );
    const {panelProps} = props;
    // const {addPanel} = useContext(PanelExportUpdaterContext);
    const node = Panel2.inputIsSingle(panelProps.input)
      ? ((panelProps.input.path as any) as CG.Node)
      : undefined;
    // const [hovering, setHovering] = useState(false);
    return (
      <Popup
        trigger={
          // we need the <span> so that Popup can find the position of IconButton when it's wrapped
          // in a styled component -- tried <Ref> but it didn't work
          <span>
            <S.IconButton>
              <S.DevQueryIcon style={{transform: 'translateY(-4px)'}} />
            </S.IconButton>
          </span>
        }
        hoverable
        content={
          <div
            style={{
              maxHeight: 400,
              maxWidth: 1200,
              overflow: 'auto',
              fontSize: 14,
              whiteSpace: 'nowrap',
            }}>
            <div>
              <span>
                <span style={{fontWeight: 'bold', marginRight: 8}}>Query</span>
                <span
                  style={{
                    cursor: 'pointer',
                    borderBottom:
                      queryVisType === 'string' ? '1px solid #888' : undefined,
                  }}
                  onClick={() => setQueryVisType('string')}>
                  string
                </span>
                {' | '}
                <span
                  style={{
                    cursor: 'pointer',
                    borderBottom:
                      queryVisType === 'dag' ? '1px solid #888' : undefined,
                  }}
                  onClick={() => setQueryVisType('dag')}>
                  dag
                </span>
              </span>{' '}
              {node != null ? (
                queryVisType === 'string' ? (
                  <pre style={{fontSize: 12}}>{HL.toString(node)}</pre>
                ) : (
                  <ComputeGraphViz node={node} width={600} height={300} />
                )
              ) : (
                // <ComputeGraphViz node={node} width={600} height={300} />
                <span>TODO: Node not available</span>
              )}
            </div>
            <div>
              <span style={{fontWeight: 'bold'}}>Query result type</span>{' '}
              {Types.toString(panelProps.inputType)}
            </div>
            <div>
              <span style={{fontWeight: 'bold'}}>Panel</span>{' '}
              {panelProps.panelSpec.id}
            </div>
            {panelProps.config != null && (
              <div>
                <span style={{fontWeight: 'bold'}}>Config</span>{' '}
                {JSON.stringify(panelProps.config, undefined, 2)}
              </div>
            )}
            <Button
              style={{marginTop: 16, padding: '4px 8px'}}
              onClick={() => {
                throw new Error('EXPORT NOT IMPLEMNETED');
                // addPanel({
                //   queryPath: query,
                //   panelSpec: panelProps.panelSpec,
                // })
              }}>
              Export
            </Button>
          </div>
        }
      />
    );
  },
  {
    id: 'DevQueryPopup',
  }
);
